import React, {useState} from "react";
import {
  Box,
  ClickAwayListener,
  IconButton,
  Popper,
  makeStyles,
} from "@material-ui/core";
import type {PopperPlacementType} from "@material-ui/core/Popper";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {grey} from "@material-ui/core/colors";

const useStyles = makeStyles((theme) => ({
  popper: {
    // Above drawers/modals — same layer MUI reserves for tooltips.
    zIndex: theme.zIndex.tooltip,
  },
  bubble: {
    // Mirrors the MUI tooltip look so it reads as a native hint.
    backgroundColor: "rgba(97, 97, 97, 0.92)",
    color: theme.palette.common.white,
    borderRadius: 4,
    padding: theme.spacing(0.5, 1),
    fontSize: 12,
    lineHeight: 1.4,
    maxWidth: 300,
    margin: theme.spacing(0.5, 0),
  },
}));

type Props = {
  /** Hint content shown when the icon is clicked. */
  title: NonNullable<React.ReactNode>;
  placement?: PopperPlacementType;
  className?: string;
};

/**
 * ⓘ button whose hint opens ONLY on an explicit click. Built on a bare
 * `Popper` (not `Tooltip`) on purpose — there is no hover/focus/touch
 * trigger machinery at all, the bubble exists strictly while the click
 * state holds it open. Closes on a second click or any click-away; the
 * click never bubbles up, so it is safe inside clickable rows.
 */
const InfoClickTooltip: React.FC<Props> = ({
  title,
  placement = "top-end",
  className,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
      <span className={className}>
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl((prev) => (prev ? null : event.currentTarget));
          }}
        >
          <InfoOutlinedIcon fontSize="small" htmlColor={grey[500]} />
        </IconButton>
        <Popper
          open={!!anchorEl}
          anchorEl={anchorEl}
          placement={placement}
          className={classes.popper}
        >
          <Box className={classes.bubble}>{title}</Box>
        </Popper>
      </span>
    </ClickAwayListener>
  );
};

export default InfoClickTooltip;
